/**
 * Centralized rate limit types and interfaces
 */

export interface RateLimitError extends Error {
  status: number;
  limit?: number | null;
  used?: number | null;
  remaining?: number | null;
  isMonthlyLimit?: boolean;
  isRateLimit?: boolean;
}

export interface RateLimitInfo {
  limit: number | null;
  used: number | null;
  remaining: number | null;
  exceeded: boolean;
  isMonthlyLimit?: boolean;
}

export interface RateLimitResponse {
  error: string;
  limit?: number | null;
  used?: number | null;
  remaining?: number | null;
}

/**
 * Checks if an error is a rate limit error (429 status or rate limit indicators)
 */
export function isRateLimitError(error: unknown): error is RateLimitError {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as Record<string, unknown>;
  
  // Check for 429 status code
  if (err.status === 429) return true;
  
  // Check error message for rate limit indicators
  const message = typeof err.message === 'string' ? err.message.toLowerCase() : '';
  if (!message) return false;
  
  const rateLimitIndicators = [
    'rate limit',
    'ratelimit',
    'monthly limit',
    'limit has been exceeded',
    'error code: 429',
    'error_code: 429',
    'exceeded the token rate limit',
    'rate limit reached',
    'ratelimitreached',
    'too many requests',
  ];
  
  return rateLimitIndicators.some(indicator => message.includes(indicator));
}

/**
 * Extracts rate limit information from an error object
 */
export function extractRateLimitInfo(error: unknown, response?: Response): RateLimitInfo | null {
  if (!isRateLimitError(error)) return null;
  
  const err = error as RateLimitError;
  let limit: number | null = err.limit ?? null;
  let used: number | null = err.used ?? null;
  let remaining: number | null = err.remaining ?? null;
  
  // Try to extract from response body if available
  if (response && (limit === null || used === null || remaining === null)) {
    try {
      // Note: Response body can only be read once, so we need to clone
      // But this might fail if already consumed - handled in useRateLimit
      const clonedResponse = response.clone();
      clonedResponse.json().then((data: RateLimitResponse) => {
        if (data.limit !== undefined && limit === null) limit = data.limit;
        if (data.used !== undefined && used === null) used = data.used;
        if (data.remaining !== undefined && remaining === null) remaining = data.remaining;
      }).catch(() => {
        // Ignore - response might already be consumed
      });
    } catch {
      // Ignore parse errors
    }
  }
  
  // Try to parse from error message if it contains JSON
  if (err.message && (limit === null || used === null || remaining === null)) {
    try {
      const jsonMatch = err.message.match(/\{.*\}/);
      if (jsonMatch) {
        const data = JSON.parse(jsonMatch[0]) as RateLimitResponse;
        if (data.limit !== undefined && limit === null) limit = data.limit;
        if (data.used !== undefined && used === null) used = data.used;
        if (data.remaining !== undefined && remaining === null) remaining = data.remaining;
      }
    } catch {
      // Ignore parse errors
    }
  }
  
  return {
    limit,
    used,
    remaining,
    exceeded: true,
    isMonthlyLimit: err.isMonthlyLimit ?? false,
  };
}

/**
 * Creates a standardized rate limit error object
 */
export function createRateLimitError(
  message: string,
  info?: Partial<RateLimitInfo>,
  response?: Response
): RateLimitError {
  const error = new Error(message) as RateLimitError;
  error.status = response?.status ?? 429;
  error.limit = info?.limit ?? null;
  error.used = info?.used ?? null;
  error.remaining = info?.remaining ?? null;
  error.isMonthlyLimit = info?.isMonthlyLimit ?? false;
  error.isRateLimit = true;
  return error;
}

